import {
  HISTORY_FIELD_META,
  formatScalarValue,
  humanizeFieldKey,
} from "./historyFieldLabels";

const SENSOR_FIELDS = [
  "orpConnected",
  "phConnected",
  "salinityConnected",
  "tempConnected",
  "isVsdPumpConnected",
] as const;

const ACCESSORY_FIELDS = ["acidPump", "heatPump", "heatpump"] as const;

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function formatTargetLines(payload: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const connected: string[] = [];
  const disconnected: string[] = [];

  for (const [key, value] of Object.entries(payload)) {
    if (SENSOR_FIELDS.includes(key as (typeof SENSOR_FIELDS)[number])) {
      const label =
        HISTORY_FIELD_META[key]?.label.replace(/ sensor$/, "") ??
        humanizeFieldKey(key);
      if (value === true) connected.push(label);
      else if (value === false) disconnected.push(label);
      continue;
    }

    if (ACCESSORY_FIELDS.includes(key as (typeof ACCESSORY_FIELDS)[number])) {
      continue;
    }

    const formatted = formatScalarValue(key, value);
    if (!formatted || formatted === "cleared") continue;
    const label = HISTORY_FIELD_META[key]?.label ?? humanizeFieldKey(key);
    lines.push(`${label}: ${formatted}`);
  }

  if (connected.length > 0) {
    lines.push(`Connected: ${connected.join(", ")}`);
  }
  if (disconnected.length > 0) {
    lines.push(`Disconnected: ${disconnected.join(", ")}`);
  }

  return lines;
}

function formatAccessoryLines(payload: Record<string, unknown>): string[] {
  const lines: string[] = [];

  for (const key of ACCESSORY_FIELDS) {
    if (!(key in payload)) continue;
    const value = payload[key];
    const label = HISTORY_FIELD_META[key]?.label ?? humanizeFieldKey(key);

    if (isEmptyValue(value) || value === false) {
      lines.push(`${label} cleared`);
    } else if (value === true) {
      lines.push(`${label} enabled`);
    } else {
      lines.push(`${label} set to ${value}`);
    }
  }

  return lines;
}

/** Build a readable multi-line summary from a history payload object. */
export function formatPayloadSummary(
  payload: Record<string, unknown>,
): string | null {
  const keys = Object.keys(payload);
  if (keys.length === 0) return null;

  const hasTargets = keys.some(
    (key) =>
      key in HISTORY_FIELD_META &&
      !ACCESSORY_FIELDS.includes(key as (typeof ACCESSORY_FIELDS)[number]),
  );
  const hasAccessories = ACCESSORY_FIELDS.some((key) => key in payload);

  const lines: string[] = [];
  if (hasTargets) lines.push(...formatTargetLines(payload));
  if (hasAccessories) lines.push(...formatAccessoryLines(payload));

  if (lines.length === 0) {
    for (const [key, value] of Object.entries(payload)) {
      if (isEmptyValue(value)) continue;
      if (typeof value === "object") continue;
      const label = HISTORY_FIELD_META[key]?.label ?? humanizeFieldKey(key);
      const formatted = formatScalarValue(key, value);
      if (formatted) lines.push(`${label}: ${formatted}`);
    }
  }

  return lines.length > 0 ? lines.join("\n") : null;
}

/** Extract JSON object from "... with payload {...}" legacy request strings. */
export function extractPayloadFromRequest(
  request: string,
): Record<string, unknown> | null {
  const match = request.match(/with payload\s*(\{[\s\S]*\})\s*$/i);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}
